/**
 * Frontend observability — mirror of backend logger.py.
 *
 * SOLID:
 *   SRP — Logger only builds LogRecords and dispatches to sinks.
 *   OCP — New destinations = new LogSink implementation.
 *   LSP — Any sink substitutes any other.
 *   ISP — LogSink has one method: write().
 *   DIP — Logger depends on the LogSink interface, not concrete sinks.
 *
 * Usage:
 *   import { getLogger, configure } from "./observability/logger";
 *   import { ConsoleSink, MemorySink, ServerSink } from "./observability/sinks";
 *
 *   configure([new ConsoleSink(), new MemorySink(), new ServerSink("/api/debug-log")]);
 *   const log = getLogger("chat.stream");
 *   log.info("chat.send", { chars: 42 });
 */

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogRecord {
  timestamp: number; // epoch seconds
  level: LogLevel;
  logger: string;
  message: string;
  extra?: Record<string, unknown>;
  context?: Record<string, unknown>;
  source: "frontend";
}

export interface LogSink {
  write(record: LogRecord): void;
}

// ---------------------------------------------------------------------------
// Context propagation (run_id, section_id, etc.)
// ---------------------------------------------------------------------------

let _context: Record<string, unknown> = {};

export function setContext(patch: Record<string, unknown>): void {
  _context = { ..._context, ...patch };
}

export function clearContext(): void {
  _context = {};
}

export function getContext(): Record<string, unknown> {
  return { ..._context };
}

// ---------------------------------------------------------------------------
// Sink registry + Logger
// ---------------------------------------------------------------------------

let _sinks: LogSink[] = [];
const _loggers: Map<string, Logger> = new Map();

export function configure(sinks: LogSink[]): void {
  _sinks = [...sinks];
}

export function getLogger(name: string): Logger {
  let l = _loggers.get(name);
  if (!l) {
    l = new Logger(name);
    _loggers.set(name, l);
  }
  return l;
}

export class Logger {
  constructor(private readonly name: string) {}

  debug(message: string, extra?: Record<string, unknown>): void {
    this._emit("debug", message, extra);
  }
  info(message: string, extra?: Record<string, unknown>): void {
    this._emit("info", message, extra);
  }
  warn(message: string, extra?: Record<string, unknown>): void {
    this._emit("warn", message, extra);
  }
  error(message: string, extra?: Record<string, unknown>): void {
    this._emit("error", message, extra);
  }

  private _emit(level: LogLevel, message: string, extra?: Record<string, unknown>): void {
    const record: LogRecord = {
      timestamp: Date.now() / 1000,
      level,
      logger: this.name,
      message,
      extra,
      context: getContext(),
      source: "frontend",
    };
    for (const sink of _sinks) {
      try {
        sink.write(record);
      } catch {
        // Sinks must not break logging
      }
    }
  }
}
